import { inject, Injectable } from '@angular/core';
import { finalize, Observable, take } from 'rxjs';
import { OneMapAddress, OneMapApiService } from '@core/one-map';
import { LoadingService } from '@portal/loading/data-access';
import { PortalFieldConfig } from '@portal/form/data-access';
import { AddressModelKeys } from '../const/form-field-key.const';
import { COMMON_VALIDATION, ENTITY_NAME } from '@core/data-access';

@Injectable({ providedIn: 'root' })
export class AddressFormConfigService {
  private readonly oneMapApi = inject(OneMapApiService);
  private readonly loadingService = inject(LoadingService);

  initLocalAddressFormFields(isResidentialAddress: boolean): PortalFieldConfig[] {
    return [
      {
        key: AddressModelKeys.postalCode,
        className: 'col-start-1',
        type: 'input',
        props: {
          label: 'Postal Code',
          maskConfig: { mask: '000000' },
          placeholder: 'e.g. 57212',
          minLength: 3,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        className: 'col-start-1',
        type: 'single-button',
        expressions: { hide: field => this.disableControl(field, isResidentialAddress) || field.options?.formState?.disabled === true },
        props: {
          type: 'get-address',
          label: 'Get Address',
          click: ({ model, props, form }) => {
            const postal = model?.[AddressModelKeys.postalCode];
            if (!postal) return;

            props && (props.disabled = true);

            this.loadingService.show();
            this.getOneMapAddress(postal)
              .pipe(
                take(1),
                finalize(() => this.loadingService.hide()),
              )
              .subscribe(address => {
                props && (props.disabled = false);
                // console.log({ address });

                form?.get(AddressModelKeys.postalCode)?.setValue(address?.postalCode);
                form?.get(AddressModelKeys.blockNumber)?.setValue(address?.blockNumber);
                form?.get(AddressModelKeys.buildingName)?.setValue(address?.buildingName);
                form?.get(AddressModelKeys.streetName)?.setValue(address?.streetName);
              });
          },
        },
      },
      {
        key: AddressModelKeys.blockNumber,
        className: 'col-start-1',
        type: 'input',
        props: {
          label: 'Block/House Number',
          maskConfig: { mask: 'A*' },
          placeholder: 'e.g. 371A',
          maxLength: 8,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.streetName,
        type: 'input',
        props: {
          label: 'Street Name',
          placeholder: 'e.g. Cavan Road',
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: ENTITY_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.storey,
        type: 'input',
        props: {
          label: 'Storey',
          placeholder: 'e.g. 7',
          maskConfig: { mask: '0*' },
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.unitNumber,
        type: 'input',
        props: {
          label: 'Unit Number',
          placeholder: 'e.g. 12',
          maskConfig: { mask: 'A*' },
          maxLength: 6,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.buildingName,
        type: 'input',
        props: {
          label: 'Building Name',
          placeholder: 'e.g. Casa del Rio',
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: ENTITY_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
    ];
  }

  initForeignAddressFormFields(isResidentialAddress: boolean): PortalFieldConfig[] {
    return [
      {
        key: AddressModelKeys.countryId,
        className: 'col-start-1',
        type: '#country',
        props: { required: true },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.addressLine1,
        className: 'col-start-1',
        type: 'input',
        props: {
          label: 'Foreign Address Line 1',
          placeholder: 'e.g. 22 Rosey Lane',
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          required: true,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.addressLine2,
        type: 'input',
        props: {
          label: 'Foreign Address Line 2',
          placeholder: 'e.g. 22 Jalan PJU 5',
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
      {
        key: AddressModelKeys.postalCode,
        className: 'col-start-1',
        type: 'input',
        props: {
          label: 'Postal Code',
          maskConfig: { mask: '000000' },
          placeholder: 'e.g. 57212',
          minLength: 3,
          required: true,
        },
        expressions: { 'props.disabled': field => this.disableControl(field, isResidentialAddress) },
      },
    ];
  }

  private getOneMapAddress(postal: string): Observable<OneMapAddress | null> {
    return this.oneMapApi.get(postal);
  }

  private disableControl(field: PortalFieldConfig, isResidentialAddress: boolean): boolean {
    return field.options?.formState?.isMyInfo && isResidentialAddress;
  }
}
